import {
  initialize as initializeLD,
  type LDClient,
  type LDFlagChangeset,
  type LDUser,
} from 'launchdarkly-js-client-sdk';
import { isEqual, mapValues } from 'lodash';
import { type Store } from 'redux';

import type { User } from '../auth';
import config from '../config';
import { type AppState } from '../reducer';
import { camelizeKeys } from '../shared/utils';

import { Actions } from './actions';
import { syncBillingFlagOverrides } from './billingFlagOverrides';
import { type Settings } from './model';

type MaybeUser = User | null;
type UserSelector = (state: AppState) => MaybeUser;
type UserChangeHandler = (user: MaybeUser) => void;

// oxlint-disable-next-line import/no-mutable-exports
export let client: LDClient | null = null;

// XXX: property names must be the camel-case version of the flag key, to make sure flag updates register properly
const evaluateFlags = (c: LDClient): Partial<Settings> => ({
  pricingCalculator: c.variation('pricing-calculator'),
  enableHighUsageChart: c.variation('enable-high-usage-charts'),
  enableOverages: c.variation('enable-overages'),
  recentlyViewedAccounts: c.variation('recently-viewed-accounts'),
  removeServerConnections: c.variation('remove-server-connections'),
  enableMemberSessions: c.variation('enable-member-sessions'),
  dataExportPercentagePricing: c.variation('data-export-percentage-pricing'),
  enablePaginationOfMembers: c.variation('enable-pagination-of-members'),
  enableHoneycombLinks: c.variation('enable-honeycomb-links'),
  enableExtendingEnterpriseTrialCampaign: c.variation('enable-extending-enterprise-trial-campaign'),
  enableExtendingGuardianTrialCampaign: c.variation('enable-extending-guardian-trial-campaign'),
  displayImmediatelyActionableProductsInAccountProvisionReview: c.variation(
    'display-immediately-actionable-products-in-account-provision-review',
  ),
  enableTeamsVisibility: c.variation('enable-teams-visibility', 'teams_off'),
  enableSubscriptionStateFiltering: c.variation('filter-accounts-by-subscription-state', false),
  enableCancelImmediatelyFunctionality: c.variation('enable-cancel-immediately-functionality', false),
  enableGeneralTab: c.variation('enable-general-tab', false),
  enableProvisioningModeToggle: c.variation('show-provisioning-mode-toggle', false),
  enableReactQueryDevtools: c.variation('enable-react-query-devtools', false),
  enableUsageChartsV2: c.variation('enable-usage-charts-v2', false),
  enableSdkVersionUsageDetails: c.variation('enable-sdk-version-usage-details', false),
  enableRoleAndTeamFilter: c.variation('enable-role-and-team-filter', false),
  enableRolesAndTeamsTab: c.variation('enable-roles-and-teams-tab', false),
  enablePowerUsersTab: c.variation('enable-power-users-tab', false),
  enableVitalAuditLogEnhancedSearch: c.variation('vitalstatistix-audit-log-enhanced-search', true),
  enableUpdatedRoleDiffs: c.variation('enable-updated-role-diffs', false),
  enableSelfServeDeveloper2025PlanSelection: c.variation('enable-self-serve-developer-2025-plan-selection', false),
  enableSelfServeFoundation2025PlanSelection: c.variation('enable-self-serve-foundation-2025-plan-selection', false),
  enabledAuditlogJsonExport: c.variation('enabled-auditlog-json-export', false),
  enableAuditLogActorDisplay: c.variation('enable-audit-log-actor-display', false),
  enableAuditLogEntryLinks: c.variation('enable-audit-log-entry-links', false),
  enablePlanTypeInSearch: c.variation('enable-plan-type-in-search', false),
  enableRelatedIdAccountSearch: c.variation('enable-related-id-account-search', false),
  enableAuthMethodColumn: c.variation('enable-auth-method-column', false),
  enableMemberMfaReset: c.variation('enable-member-mfa-reset', false),
  enableVitalResourceCountLimits: c.variation('enable-vital-resource-count-limits', false),
  enableTrialInvitationHistory: c.variation('enable-trial-invitation-history', false),
  vitalstatistixDataExportObservability: c.variation('vitalstatistix-data-export-observability', false),
});

const observeStore = (store: Store<AppState>, select: UserSelector, onChange: UserChangeHandler) => {
  let currentState: MaybeUser;

  const handleChange = () => {
    const nextState = select(store.getState());
    if (!isEqual(currentState, nextState)) {
      currentState = nextState;
      onChange(currentState);
    }
  };

  const unsubscribe = store.subscribe(handleChange);
  handleChange();
  return unsubscribe;
};

const userToLDUser: (user?: User) => LDUser = (user) =>
  user
    ? {
        key: user.user_id,
        email: user.email,
        custom: {
          groups: user.groups,
        },
      }
    : { anonymous: true };

const publishSettings = (store: Store<AppState>, settings: Partial<Settings>) => {
  syncBillingFlagOverrides(settings);
  store.dispatch(Actions.updateSettingsPublic(settings));
};

export const initializeSettings = (store: Store<AppState>, user?: User) => {
  const ldUser = userToLDUser(user);
  const c = initializeLD(config.launchdarkly.clientId, ldUser, {
    baseUrl: config.launchdarkly.baseUrl,
    streamUrl: config.launchdarkly.streamUrl,
    eventsUrl: config.launchdarkly.eventsUrl,
    sendEventsOnlyForVariation: true,
  });

  c.on('change', (settings: LDFlagChangeset) => {
    publishSettings(store, camelizeKeys(mapValues(settings, (f) => f.current)) as Partial<Settings>);
  });

  // Immediately evaluate flags when client is ready
  c.on('ready', () => {
    const flags = evaluateFlags(c);
    console.log('LaunchDarkly client ready, evaluating Vitalstatistix flags:', flags);
    publishSettings(store, flags);
  });

  // Handle client errors
  c.on('failed', () => {
    console.warn('LaunchDarkly client failed to initialize, using default flag values');
    // Still dispatch default settings to ensure the app doesn't get stuck
    publishSettings(store, evaluateFlags(c));
  });

  const selectUser: UserSelector = (state) => state.auth.user;
  observeStore(store, selectUser, (newUser) => {
    if (!newUser) {
      return;
    }
    c.identify(userToLDUser(newUser))
      .then(() => {
        // Ensure flags are evaluated after user identification
        publishSettings(store, evaluateFlags(c));
      })
      .catch((error) => {
        console.warn('LaunchDarkly user identification failed:', error);
        // Still try to evaluate flags with the current client state
        publishSettings(store, evaluateFlags(c));
      });
  });

  store.dispatch(Actions.updateSettingsPrivate());

  client = c;
  return c;
};
